import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useCopySightSettings, type CopySightSource } from '@/hooks/useCopySightSettings';
import { Settings, Loader2, ScanSearch } from 'lucide-react';

const COPYSIGHT_SWITCHES: { source: CopySightSource; label: string; description: string }[] = [
  {
    source: 'runner',
    label: 'Runner outputs',
    description: 'Images saved to the runner bucket output/ folder (including subfolders).',
  },
  {
    source: 'onDemand',
    label: 'On-demand instance outputs',
    description: 'Images saved to users/{user}/output/ in the on-demand bucket (including subfolders).',
  },
];

export default function SettingsPage() {
  const { settings: copysight, loading, savingSource, error, setAutoInspect } = useCopySightSettings();

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Admin: Configure platform features</p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanSearch className="h-5 w-5" />
              CopySight auto-inspection
            </CardTitle>
            <CardDescription>
              Automatically inspect new image outputs for IP and faces when they are saved. Changes apply within about 30 seconds,
              with no deploy. Everything is off until enabled.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            ) : (
              COPYSIGHT_SWITCHES.map(({ source, label, description }) => (
                <div key={source} className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-1">
                    <Label htmlFor={`copysight-${source}`}>{label}</Label>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    id={`copysight-${source}`}
                    checked={copysight?.autoInspect[source] ?? false}
                    disabled={!copysight || savingSource === source}
                    onCheckedChange={(checked) => setAutoInspect(source, checked)}
                  />
                </div>
              ))
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {copysight?.updatedAt && (
              <p className="text-xs text-muted-foreground">Last changed: {new Date(copysight.updatedAt).toLocaleString()}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
